import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { VERIFICATION_CONFIG } from '@the-wall/shared';
import { useAuthStore } from '@/stores/authStore';
import { api, ApiError } from '@/services/api';
import { showSnackbar } from '@/stores/snackbarStore';

const COOLDOWN_STORAGE_KEY = 'verify-email-cooldown-until';

function getCooldownSecondsRemaining(): number {
  const until = sessionStorage.getItem(COOLDOWN_STORAGE_KEY);
  if (!until) return 0;
  const remaining = Math.ceil((Number(until) - Date.now()) / 1000);
  return remaining > 0 ? remaining : 0;
}

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(getCooldownSecondsRemaining);

  const verified = searchParams.get('verified') === '1';
  const error = searchParams.get('error');

  useEffect(() => {
    if (verified && token) {
      checkAuth();
    }
  }, [verified, token, checkAuth]);

  // Tick down cooldown every second (effect re-runs only when crossing 0 ↔ positive)
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const id = setInterval(() => {
      setCooldownSeconds((s) => {
        if (s <= 1) {
          sessionStorage.removeItem(COOLDOWN_STORAGE_KEY);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownSeconds > 0]);

  const handleResend = async () => {
    if (!token) return;
    setResendLoading(true);
    try {
      await api.sendVerificationEmail(token);
      setResendSent(true);
      setCooldownSeconds(VERIFICATION_CONFIG.RESEND_COOLDOWN_SECONDS);
      sessionStorage.setItem(
        COOLDOWN_STORAGE_KEY,
        String(Date.now() + VERIFICATION_CONFIG.RESEND_COOLDOWN_SECONDS * 1000)
      );
      showSnackbar('Verification email sent. Check your inbox.', 'success');
    } catch (e) {
      if (e instanceof ApiError && e.status === 429 && e.retryAfterSeconds != null) {
        setCooldownSeconds(e.retryAfterSeconds);
        sessionStorage.setItem(
          COOLDOWN_STORAGE_KEY,
          String(Date.now() + e.retryAfterSeconds * 1000)
        );
        showSnackbar(
          `Please wait ${e.retryAfterSeconds}s before requesting another email.`,
          'error'
        );
      } else {
        const msg = e instanceof Error ? e.message : 'Failed to send';
        showSnackbar(msg, 'error');
      }
    } finally {
      setResendLoading(false);
    }
  };

  if (verified) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
        <div className="bg-wall-card border border-wall-border rounded-xl p-8 max-w-md w-full text-center shadow-lg">
          <div className="text-4xl mb-4">✓</div>
          <h1 className="text-xl font-semibold text-wall-text mb-2">Email verified</h1>
          <p className="text-wall-muted mb-6">
            Your email is verified. Your account is easier to recover and ready for the Wall.
          </p>
          <Link
            to="/"
            className="inline-block bg-wall-accent text-wall-bg px-6 py-2 rounded-lg font-medium hover:opacity-90"
          >
            Back to the Wall
          </Link>
        </div>
      </div>
    );
  }

  if (error === 'invalid_or_expired') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
        <div className="bg-wall-card border border-wall-border rounded-xl p-8 max-w-md w-full text-center shadow-lg">
          <h1 className="text-xl font-semibold text-wall-text mb-2">Link invalid or expired</h1>
          <p className="text-wall-muted mb-6">
            This verification link is invalid or has expired. Request a new one below.
          </p>
          {token && user?.authProvider === 'email' && !user?.emailVerifiedAt && (
            <button
              type="button"
              onClick={handleResend}
              disabled={resendLoading || cooldownSeconds > 0}
              className="bg-wall-accent text-wall-bg px-6 py-2 rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
            >
              {resendLoading
                ? 'Sending…'
                : cooldownSeconds > 0
                  ? `Request again in ${cooldownSeconds}s`
                  : resendSent
                    ? 'Check your email'
                    : 'Send new verification email'}
            </button>
          )}
          <Link to="/" className="block mt-4 text-wall-accent hover:underline">
            Back to the Wall
          </Link>
        </div>
      </div>
    );
  }

  if (error === 'missing_token') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
        <div className="bg-wall-card border border-wall-border rounded-xl p-8 max-w-md w-full text-center shadow-lg">
          <h1 className="text-xl font-semibold text-wall-text mb-2">Missing verification link</h1>
          <p className="text-wall-muted mb-6">
            Open the verification link from your email, or request a new one if you're logged in.
          </p>
          <Link to="/" className="text-wall-accent hover:underline">
            Back to the Wall
          </Link>
        </div>
      </div>
    );
  }

  // No query params: show generic "check your email" or "resend" if logged in as unverified email user
  const canResend = token && user?.authProvider === 'email' && !user?.emailVerifiedAt;

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
      <div className="bg-wall-card border border-wall-border rounded-xl p-8 max-w-md w-full text-center shadow-lg">
        <h1 className="text-xl font-semibold text-wall-text mb-2">Verify your email</h1>
        <p className="text-wall-muted mb-6">
          {canResend
            ? 'Check your inbox for the verification link. Painting stays open while you verify.'
            : 'Open the verification link we sent to your email to verify your account.'}
        </p>
        {canResend && (
          <button
            type="button"
            onClick={handleResend}
            disabled={resendLoading || cooldownSeconds > 0}
            className="bg-wall-accent text-wall-bg px-6 py-2 rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
          >
            {resendLoading
              ? 'Sending…'
              : cooldownSeconds > 0
                ? `Request again in ${cooldownSeconds}s`
                : resendSent
                  ? 'Check your email'
                  : 'Resend verification email'}
          </button>
        )}
        <Link to="/" className="block mt-4 text-wall-accent hover:underline">
          Back to the Wall
        </Link>
      </div>
    </div>
  );
}
